import { create } from "zustand";
import type { TFillRef } from "@libs/figure-spans";

// An image on a reader's message. `url` is null while the server cannot hand out a view link yet
// (the upload's malware scan has not passed), so the chat shows the name instead.
export type TLeoMessageImage = { name: string; url: string | null };

export type TLeoChatMessage = {
  role: "user" | "assistant";
  content: string;
  isError?: boolean;
  appliedChapters?: string[];
  // The slides whose page the turn changed, by position (1 is the first).
  appliedSlides?: number[];
  estCostUsd?: number | null;
  refs?: TFillRef[] | null;
  images?: TLeoMessageImage[];
};

// What Leo is doing right now, read from the runner's last action; `slug` is the chapter file.
export type TLeoStatus =
  | { kind: "reading"; chapter: string; slug: string }
  | { kind: "editing"; chapter: string; slug: string }
  | { kind: "readingSlide"; slide: number }
  | { kind: "editingSlide"; slide: number }
  | { kind: "designing" }
  | { kind: "searching"; query: string }
  | { kind: "replying" }
  | { kind: "thinking" }
  | { kind: "starting" };

export type TLeoChatTaskState = {
  id: string;
  canEdit: boolean;
  status: "pending" | "running" | "succeeded" | "failed";
  progressText: string | null;
  startedAt: number;
  liveStatus: TLeoStatus | null;
  // Slugs of every chapter Leo has edited in this task. Its edits land only when the task
  // finishes, so those chapters stay marked as changing until then.
  editedChapters: string[];
  editedHome: boolean;
};

export type TLeoTaskActivity = {
  liveStatus: TLeoStatus;
  editedChapters: string[];
  editedHome: boolean;
};

type TLeoChatState = {
  historyByProject: Record<string, TLeoChatMessage[]>;
  activeTaskByProject: Record<string, TLeoChatTaskState | null>;
  // A turn sent but not yet accepted by the backend, which takes a few seconds.
  startingByProject: Record<string, boolean>;
  // The finished task whose changes are still being loaded onto the page.
  refreshingTaskByProject: Record<string, string | null>;
  setStarting: (projectId: string, isStarting: boolean) => void;
  addMessage: (projectId: string, message: TLeoChatMessage) => void;
  setActiveTask: (projectId: string, task: TLeoChatTaskState | null) => void;
  trackTaskActivity: (projectId: string, taskId: string, activity: TLeoTaskActivity) => void;
  startRefresh: (projectId: string, taskId: string) => void;
  endRefresh: (projectId: string, taskId: string) => void;
  clearHistory: (projectId: string) => void;
  setHistory: (projectId: string, messages: TLeoChatMessage[]) => void;
};

const EMPTY_MESSAGES: TLeoChatMessage[] = [];

export const useLeoChatStore = create<TLeoChatState>()((set) => ({
  historyByProject: {},
  activeTaskByProject: {},
  startingByProject: {},
  refreshingTaskByProject: {},
  setStarting: (projectId, isStarting) =>
    set((state) => ({ startingByProject: { ...state.startingByProject, [projectId]: isStarting } })),
  addMessage: (projectId, message) =>
    set((state) => ({
      historyByProject: {
        ...state.historyByProject,
        [projectId]: [...(state.historyByProject[projectId] ?? EMPTY_MESSAGES), message]
      }
    })),
  setActiveTask: (projectId, task) =>
    set((state) => ({
      activeTaskByProject: { ...state.activeTaskByProject, [projectId]: task }
    })),
  trackTaskActivity: (projectId, taskId, { liveStatus, editedChapters: edited, editedHome }) =>
    set((state) => {
      const task = state.activeTaskByProject[projectId];
      if (!task || task.id !== taskId) return state;
      const added = edited.filter(
        (slug, index) => !task.editedChapters.includes(slug) && edited.indexOf(slug) === index
      );
      const editedChapters = added.length > 0 ? [...task.editedChapters, ...added] : task.editedChapters;
      return {
        activeTaskByProject: {
          ...state.activeTaskByProject,
          [projectId]: {
            ...task,
            liveStatus,
            editedChapters,
            editedHome: task.editedHome || editedHome
          }
        }
      };
    }),
  startRefresh: (projectId, taskId) =>
    set((state) => ({ refreshingTaskByProject: { ...state.refreshingTaskByProject, [projectId]: taskId } })),
  endRefresh: (projectId, taskId) =>
    set((state) =>
      state.refreshingTaskByProject[projectId] === taskId
        ? { refreshingTaskByProject: { ...state.refreshingTaskByProject, [projectId]: null } }
        : state
    ),
  clearHistory: (projectId) =>
    set((state) => ({
      historyByProject: { ...state.historyByProject, [projectId]: EMPTY_MESSAGES },
      activeTaskByProject: { ...state.activeTaskByProject, [projectId]: null }
    })),
  setHistory: (projectId, messages) =>
    set((state) => ({
      historyByProject: { ...state.historyByProject, [projectId]: messages }
    }))
}));

export const selectIsLeoEditingChapter =
  (projectId: string, slug: string) =>
  (state: TLeoChatState): boolean =>
    !!state.activeTaskByProject[projectId]?.editedChapters.includes(slug);

// A turn that only answers changes nothing, so the analysis counts as being updated from Leo's
// first edit, not from the moment the message is sent.
export const isLeoChangingContent = (task: TLeoChatTaskState | null | undefined): boolean =>
  !!task && task.canEdit && (task.editedChapters.length > 0 || task.editedHome);

// What the page shows stays out of date until the finished turn's changes have been loaded.
export const selectIsLeoUpdatingAnalysis =
  (projectId: string) =>
  (state: TLeoChatState): boolean =>
    isLeoChangingContent(state.activeTaskByProject[projectId]) || !!state.refreshingTaskByProject[projectId];
